import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { StreamingUgcPlatformRow } from 'fym-shared/src/music24/streamingSections';
import { TypedMemo } from '../../../../common/types';
import { DeltaPill } from '../../../components/primitives/DeltaPill';
import { changePctOf, formatChangePct, formatStreamingNumber } from '../../streamingFormat';
import { shortVideoMessages as messages } from '../../shortVideoMessages';
import { StreamingBlock } from '../../parts/StreamingBlock';
import { UGC_PLATFORM_LOOKS } from './ugcPlatforms';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole screen; each part uses its own slice of it */
import shared from '../../Streaming.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface UgcPlatformTableProps {
  platforms: readonly StreamingUgcPlatformRow[];
}

/** "By platform" (design streaming-sections c1). A number that a platform does not give is a dash. */
const UgcPlatformTableComponent: React.FC<UgcPlatformTableProps> = ({ platforms }) => {
  const intl = useIntl();
  const rows = useMemo(
    () => [...platforms].sort((left, right) => (right.creations ?? 0) - (left.creations ?? 0)),
    [platforms]
  );
  return (
    <StreamingBlock title={intl.formatMessage(messages.byPlatform)} testId="streaming-shortVideo-platforms">
      <div className={shared.tableWrap}>
        <table className={shared.table}>
          <thead>
            <tr>
              <th>{intl.formatMessage(messages.platformColumn)}</th>
              <th className={shared.numCell}>{intl.formatMessage(messages.videosColumn)}</th>
              <th className={shared.numCell}>{intl.formatMessage(messages.views)}</th>
              <th className={shared.numCell}>{intl.formatMessage(messages.likes)}</th>
              <th className={shared.numCell}>{intl.formatMessage(messages.shares)}</th>
              <th className={shared.numCell}>{intl.formatMessage(messages.changeColumn)}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const changePct = changePctOf(row.creations, row.previousCreations);
              const change = formatChangePct(intl, changePct);
              return (
                <tr key={row.platform}>
                  <td>
                    <span className={shared.cc}>{UGC_PLATFORM_LOOKS[row.platform].code}</span>{' '}
                    {UGC_PLATFORM_LOOKS[row.platform].name}
                  </td>
                  <td className={shared.numCell}>{formatStreamingNumber(intl, row.creations)}</td>
                  <td className={shared.numCell}>{formatStreamingNumber(intl, row.views)}</td>
                  <td className={shared.numCell}>{formatStreamingNumber(intl, row.likes)}</td>
                  <td className={shared.numCell}>{formatStreamingNumber(intl, row.shares)}</td>
                  <td className={shared.numCell}>
                    {change !== null && changePct !== null ? <DeltaPill value={changePct} label={change} /> : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </StreamingBlock>
  );
};

export const UgcPlatformTable = TypedMemo(UgcPlatformTableComponent);
